import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { parseExcel, EXCEL_MAX_SIZE_BYTES } from "@shared/utils/excel";
import { useCreatePatientsBatch, useNotifyUpload } from "./queries";
import { HttpError } from "@shared/http/client";
import type { PatientDraft } from "@/types/patient";

type StageState =
  | { stage: "idle" }
  | { stage: "parsing" }
  | { stage: "preview"; patients: PatientDraft[]; warnings: string[]; fileName: string }
  | { stage: "uploading" }
  | { stage: "done"; added: number; skipped: number; notified: number }
  | { stage: "error"; message: string };

export function CargaPage() {
  const navigate = useNavigate();
  const batchMut = useCreatePatientsBatch();
  const notifyMut = useNotifyUpload();
  const [state, setState] = useState<StageState>({ stage: "idle" });

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setState({ stage: "parsing" });
    try {
      const { patients, warnings } = await parseExcel(file);
      if (patients.length === 0) {
        setState({ stage: "error", message: "No se encontraron pacientes válidos en el archivo." });
        return;
      }
      setState({ stage: "preview", patients, warnings, fileName: file.name });
    } catch (err) {
      setState({ stage: "error", message: err instanceof Error ? err.message : "Error leyendo el archivo." });
    }
  };

  const onConfirm = async () => {
    if (state.stage !== "preview") return;
    const patients = state.patients;
    setState({ stage: "uploading" });
    try {
      const result = await batchMut.mutateAsync(patients);
      let notified = 0;
      if (result.added > 0) {
        try {
          const nr = await notifyMut.mutateAsync(result.added);
          notified = nr.sent;
        } catch {
          /* mailer opcional; ignora fallo */
        }
      }
      setState({ stage: "done", added: result.added, skipped: result.skipped, notified });
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : err instanceof Error ? err.message : "Error subiendo";
      setState({ stage: "error", message: msg });
    }
  };

  const reset = () => setState({ stage: "idle" });

  return (
    <div className="p-24">
      <h2 className="text-lg mb-16">Carga de pacientes (Excel)</h2>

      {state.stage === "idle" && (
        <div className="upload-panel">
          <p className="text-muted mb-16">
            Sube un <code>.xlsx</code> con columnas: <strong>doc</strong>, <strong>nombre</strong>, <strong>cirugia</strong>, <strong>fecha</strong> (YYYY-MM-DD o DD/MM/YYYY),{" "}
            <strong>telefono</strong>, <strong>programador</strong>. Máximo {EXCEL_MAX_SIZE_BYTES / 1024 / 1024} MB · 2000 filas.
          </p>
          <label className="btn btn-primary">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={onFileChange}
              style={{ display: "none" }}
            />
            Seleccionar archivo Excel
          </label>
          <p className="text-muted mt-16" style={{ fontSize: 12 }}>
            <a href="/PLANTILLA-CARGA-PACIENTES.xlsx" download className="link-brand">
              Descargar plantilla oficial
            </a>
          </p>
        </div>
      )}

      {state.stage === "parsing" && <div className="text-muted mt-16">Leyendo Excel…</div>}

      {state.stage === "preview" && (
        <div>
          <div className="alert-inline alert-inline--info mt-16">
            Archivo: <strong>{state.fileName}</strong> · {state.patients.length} paciente(s) detectado(s).
          </div>

          {state.warnings.length > 0 && (
            <div className="alert-inline alert-inline--warn mt-8">
              <ul className="m-0 pl-16">
                {state.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="table-scroll mt-16" style={{ maxHeight: 320 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Doc</th>
                  <th>Nombre</th>
                  <th>Cirugía</th>
                  <th>Fecha</th>
                  <th>Teléfono</th>
                  <th>Programador</th>
                </tr>
              </thead>
              <tbody>
                {state.patients.slice(0, 50).map((p, i) => (
                  <tr key={`${p.doc}-${p.fecha}-${i}`}>
                    <td>{p.doc}</td>
                    <td>{p.nombre}</td>
                    <td>{p.cirugia}</td>
                    <td>{p.fecha}</td>
                    <td>{p.telefono}</td>
                    <td>{p.programador}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {state.patients.length > 50 && (
              <div className="text-muted mt-8">…y {state.patients.length - 50} filas más (se subirán todas).</div>
            )}
          </div>

          <div className="mt-16" style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn btn-primary" onClick={onConfirm}>
              Confirmar y subir {state.patients.length} paciente(s)
            </button>
            <button type="button" className="btn btn-secondary" onClick={reset}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {state.stage === "uploading" && (
        <div className="text-muted mt-16">Subiendo pacientes…</div>
      )}

      {state.stage === "done" && (
        <div>
          <div className="alert-inline alert-inline--success alert-inline--md alert-inline--bold mt-16">
            ✅ Carga completada: {state.added} nuevo(s), {state.skipped} omitido(s) (ya existían).
            {state.notified > 0 && <> {state.notified} correo(s) enviado(s) a los evaluadores.</>}
          </div>
          <div className="mt-16" style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn btn-primary" onClick={() => navigate("/pendientes")}>
              Ver pendientes
            </button>
            <button type="button" className="btn btn-secondary" onClick={reset}>
              Cargar otro
            </button>
          </div>
        </div>
      )}

      {state.stage === "error" && (
        <div>
          <div className="alert-inline alert-inline--danger alert-inline--md alert-inline--bold mt-16">
            ❌ {state.message}
          </div>
          <div className="mt-16">
            <button type="button" className="btn btn-secondary" onClick={reset}>
              Reintentar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
